import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { X, Repeat } from "lucide-react";

const nextMonth = (ym) => { const [y, m] = ym.split("-").map(Number); return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`; };
const monthName = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("es-AR", { month: "long", year: "numeric", timeZone: "UTC" }); };

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 210 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 520, maxHeight: "88vh", overflowY: "auto" };

/** Fix BP (2026-11-03): copia el patrón del mes que se está viendo al mes siguiente (POST /schedule/repeat-month).
 * Primero vista previa (dry_run), después la copia. Nunca pisa lo ya cargado en el mes destino. */
export function ScheduleRepeatMonth({ clientId, siteId, clientName, fromMonth, onClose, onDone }) {
  const toMonth = nextMonth(fromMonth);
  const [cycle, setCycle] = useState("7");
  const [customCycle, setCustomCycle] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const cycleDays = cycle === "custom" ? Number(customCycle) : Number(cycle);

  const run = async (dryRun) => {
    if (!cycleDays || cycleDays < 1 || cycleDays > 28) { toast.error("El patrón tiene que ser de 1 a 28 días"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/repeat-month", {
        client_id: clientId, site_id: siteId || null, from_month: fromMonth, to_month: toMonth,
        cycle_days: cycleDays, dry_run: dryRun,
      }, { timeout: 180000 });
      if (dryRun) { setPreview(data); return; }
      toast.success(`${data.created} turno(s) y franco(s) copiados a ${monthName(toMonth)}`);
      if (data.blocked?.length) toast.error(`${data.blocked.length} no se copiaron por una regla bloqueante.`);
      onDone(toMonth);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose} data-testid="schedule-repeat-overlay">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="schedule-repeat-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>Repetir en el mes siguiente</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="schedule-repeat-close"><X size={18} /></button>
        </div>
        <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
          Copia el cronograma de <b style={{ textTransform: "capitalize" }}>{monthName(fromMonth)}</b> de {clientName || "este objetivo"} a <b style={{ textTransform: "capitalize" }}>{monthName(toMonth)}</b>.
          No pisa nada: si alguien ya tiene algo cargado ese día, ese día no se copia.
        </p>
        <div className="field"><label>Patrón que se repite</label>
          <select value={cycle} onChange={(e) => { setCycle(e.target.value); setPreview(null); }} data-testid="schedule-repeat-cycle">
            <option value="7">Semanal (mismo día de la semana)</option>
            <option value="6">Rotación de 6 días (ej. 4x2)</option>
            <option value="4">Rotación de 4 días (ej. 2x2)</option>
            <option value="14">Rotación de 14 días</option>
            <option value="custom">Otro (cantidad de días)</option>
          </select>
          {cycle === "custom" && (
            <input type="number" min="1" max="28" value={customCycle} onChange={(e) => { setCustomCycle(e.target.value); setPreview(null); }} placeholder="Días del ciclo" style={{ marginTop: 6 }} data-testid="schedule-repeat-custom"/>
          )}
        </div>
        {preview && (
          <div className="card" style={{ padding: 12, marginBottom: 12, background: "var(--invict-bone-2)" }} data-testid="schedule-repeat-preview">
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Se van a copiar {preview.planned} turno(s) y franco(s) de {preview.people.length} persona(s).</div>
            {preview.people.map((p) => (
              <div key={p.user_id} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span>{p.user_name}</span><span>{p.shifts} turno(s) · {p.days_off} franco(s)</span>
              </div>
            ))}
            {preview.skipped_existing > 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 6 }}>{preview.skipped_existing} día(s) ya tienen algo cargado y no se tocan.</div>}
            {preview.skipped_inactive > 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{preview.skipped_inactive} turno(s) son de personas desactivadas y no se copian.</div>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-ghost" onClick={() => run(true)} disabled={busy} style={{ flex: 1, padding: 10 }} data-testid="schedule-repeat-preview-btn">
            {busy && !preview ? "Calculando..." : "Ver vista previa"}
          </button>
          <button type="button" className="btn-primary" onClick={() => run(false)} disabled={busy || !preview || preview.planned === 0} style={{ flex: 1, padding: 10 }} data-testid="schedule-repeat-apply-btn">
            <Repeat size={13} style={{ marginRight: 6 }} />{busy && preview ? "Copiando..." : "Copiar"}
          </button>
        </div>
      </div>
    </div>
  );
}
